import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate, Link, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import { PayPalScriptProvider, PayPalButtons } from "@paypal/react-paypal-js";
import { toast } from "sonner";
import { ArrowLeft, Dog, Calendar, Clock, CheckCircle, AlertCircle, CreditCard, Building2, Banknote, Copy, Check } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CompletePaymentPage = () => {
  const { enrollmentId } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [paymentDetails, setPaymentDetails] = useState(null);
  const [paypalClientId, setPaypalClientId] = useState(null);
  const [paymentMethods, setPaymentMethods] = useState(null);
  const [processing, setProcessing] = useState(false);
  const [alreadyPaid, setAlreadyPaid] = useState(false);
  const [selectedMethod, setSelectedMethod] = useState(null);
  const [bankTransferSubmitted, setBankTransferSubmitted] = useState(false);
  const [bankDetails, setBankDetails] = useState(null);
  const [copied, setCopied] = useState(null);

  // Check if returning from Stripe
  const stripeSessionId = searchParams.get('session_id');
  const stripeMethod = searchParams.get('method');

  const fetchPaymentDetails = useCallback(async () => {
    try {
      const [detailsRes, paypalRes, methodsRes] = await Promise.all([
        axios.get(`${API}/enrollments/${enrollmentId}/payment-details`),
        axios.get(`${API}/paypal-client-id`),
        axios.get(`${API}/payment-methods`)
      ]);
      
      if (detailsRes.data.already_paid) {
        setAlreadyPaid(true);
      }
      
      setPaymentDetails(detailsRes.data);
      setPaypalClientId(paypalRes.data.client_id);
      setPaymentMethods(methodsRes.data);
    } catch (err) {
      toast.error("Failed to load payment details");
    } finally {
      setLoading(false);
    }
  }, [enrollmentId]);

  // Check Stripe payment status if returning from checkout
  const checkStripeStatus = useCallback(async () => {
    if (stripeSessionId && stripeMethod === 'stripe') {
      setProcessing(true);
      try {
        const res = await axios.get(`${API}/stripe/checkout-status/${stripeSessionId}`);
        if (res.data.payment_status === 'paid') {
          toast.success("Payment successful! Your enrollment is confirmed.");
          navigate("/thank-you", { 
            state: { 
              enrollment: paymentDetails?.enrollment,
              puppy: paymentDetails?.puppy
            } 
          });
        }
      } catch (err) {
        console.error("Stripe status check failed:", err);
      } finally {
        setProcessing(false);
      }
    }
  }, [stripeSessionId, stripeMethod, navigate, paymentDetails]);

  useEffect(() => {
    fetchPaymentDetails();
  }, [fetchPaymentDetails]);

  useEffect(() => {
    if (stripeSessionId && paymentDetails) {
      checkStripeStatus();
    }
  }, [stripeSessionId, paymentDetails, checkStripeStatus]);

  const handlePaymentSuccess = async (orderId) => {
    setProcessing(true);
    try {
      await axios.put(`${API}/enrollments/${enrollmentId}/payment`, {
        payment_id: orderId,
        payment_method: "paypal",
        amount: 440
      });
      
      toast.success("Payment successful! Your enrollment is confirmed.");
      navigate("/thank-you", { 
        state: { 
          enrollment: paymentDetails.enrollment,
          puppy: paymentDetails.puppy
        } 
      });
    } catch (err) {
      toast.error("Failed to process payment");
    } finally {
      setProcessing(false);
    }
  };

  const handleStripeCheckout = async () => {
    setProcessing(true);
    try {
      // Use public endpoint for payment link flow (no auth required)
      const res = await axios.post(`${API}/stripe/create-checkout/public`, {
        enrollment_id: enrollmentId,
        origin_url: window.location.origin
      });
      
      // Redirect to Stripe Checkout
      window.location.href = res.data.checkout_url;
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to start checkout");
      setProcessing(false);
    }
  };

  const handleBankTransfer = async () => {
    setProcessing(true);
    try {
      // Use public endpoint for payment link flow (no auth required)
      const res = await axios.post(`${API}/enrollments/${enrollmentId}/bank-transfer/public`);
      setBankDetails(res.data.bank_details);
      setBankTransferSubmitted(true);
      toast.success("Bank transfer details sent to your email!");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to process request");
    } finally {
      setProcessing(false);
    }
  };

  const copyToClipboard = (text, field) => {
    navigator.clipboard.writeText(text);
    setCopied(field);
    setTimeout(() => setCopied(null), 2000);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-cream flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-olive border-t-transparent" />
      </div>
    );
  }

  if (alreadyPaid) {
    return (
      <div className="min-h-screen bg-cream">
        <div className="max-w-xl mx-auto px-4 py-16">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="card-premium text-center py-12"
          >
            <CheckCircle className="w-16 h-16 text-olive mx-auto mb-4" />
            <h1 className="font-heading text-3xl text-charcoal mb-4">Already Paid</h1>
            <p className="text-stone mb-6">
              This enrollment has already been paid and confirmed.
            </p>
            <Link to="/login">
              <Button className="btn-primary">
                Go to Login
              </Button>
            </Link>
          </motion.div>
        </div>
      </div>
    );
  }

  if (!paymentDetails) {
    return (
      <div className="min-h-screen bg-cream">
        <div className="max-w-xl mx-auto px-4 py-16">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="card-premium text-center py-12"
          >
            <AlertCircle className="w-16 h-16 text-terracotta mx-auto mb-4" />
            <h1 className="font-heading text-3xl text-charcoal mb-4">Enrollment Not Found</h1>
            <p className="text-stone mb-6">
              We couldn&apos;t find this enrollment. The link may have expired or the enrollment may have been cancelled.
            </p>
            <Link to="/">
              <Button className="btn-primary">
                Go to Homepage
              </Button>
            </Link>
          </motion.div>
        </div>
      </div>
    );
  }

  // Bank transfer submitted view
  if (bankTransferSubmitted && bankDetails) {
    return (
      <div className="min-h-screen bg-cream">
        <header className="bg-charcoal text-white py-4">
          <div className="max-w-4xl mx-auto px-4">
            <Link to="/" className="flex items-center gap-2 text-white/80 hover:text-white transition-colors">
              <ArrowLeft className="w-4 h-4" />
              Back to Home
            </Link>
          </div>
        </header>

        <main className="max-w-2xl mx-auto px-4 py-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="card-premium"
          >
            <div className="text-center mb-8">
              <Building2 className="w-16 h-16 text-olive mx-auto mb-4" />
              <h1 className="font-heading text-3xl text-charcoal mb-2">Bank Transfer Details</h1>
              <p className="text-stone">Complete your payment using the details below</p>
            </div>

            <div className="bg-sand/50 rounded-xl p-6 space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-stone">Account Name</p>
                  <p className="font-medium text-charcoal">{bankDetails.account_name}</p>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => copyToClipboard(bankDetails.account_name, 'name')}
                  className="text-olive"
                >
                  {copied === 'name' ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                </Button>
              </div>
              
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-stone">BSB</p>
                  <p className="font-medium text-charcoal font-mono">{bankDetails.bsb}</p>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => copyToClipboard(bankDetails.bsb, 'bsb')}
                  className="text-olive"
                >
                  {copied === 'bsb' ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                </Button>
              </div>
              
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-stone">Account Number</p>
                  <p className="font-medium text-charcoal font-mono">{bankDetails.account_number}</p>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => copyToClipboard(bankDetails.account_number, 'account')}
                  className="text-olive"
                >
                  {copied === 'account' ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                </Button>
              </div>
              
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-stone">Amount</p>
                  <p className="font-heading text-2xl text-olive">${bankDetails.amount} AUD</p>
                </div>
              </div>
              
              <div className="flex items-center justify-between pt-4 border-t border-olive/10">
                <div>
                  <p className="text-sm text-stone">Reference (Important!)</p>
                  <p className="font-medium text-charcoal font-mono">{bankDetails.reference}</p>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => copyToClipboard(bankDetails.reference, 'reference')}
                  className="text-olive"
                >
                  {copied === 'reference' ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                </Button>
              </div>
            </div>

            <div className="mt-6 p-4 bg-terracotta/10 rounded-lg">
              <p className="text-sm text-charcoal">
                <strong>Important:</strong> Please use the reference above so we can match your payment. 
                We&apos;ll confirm your enrollment once we receive the payment (usually within 1-2 business days).
              </p>
            </div>

            <div className="mt-6 text-center">
              <p className="text-stone text-sm mb-4">
                A copy of these details has been sent to your email.
              </p>
              <Link to="/">
                <Button className="btn-primary">
                  Return to Home
                </Button>
              </Link>
            </div>
          </motion.div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-cream">
      {/* Header */}
      <header className="bg-charcoal text-white py-4">
        <div className="max-w-4xl mx-auto px-4">
          <Link to="/" className="flex items-center gap-2 text-white/80 hover:text-white transition-colors">
            <ArrowLeft className="w-4 h-4" />
            Back to Home
          </Link>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-8"
        >
          <h1 className="font-heading text-4xl text-charcoal mb-2">Complete Your Payment</h1>
          <p className="text-stone">Secure your spot in our program</p>
        </motion.div>

        <div className="grid md:grid-cols-2 gap-8">
          {/* Enrollment Summary */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.1 }}
            className="card-premium"
          >
            <h2 className="font-heading text-2xl text-charcoal mb-6">Enrollment Summary</h2>
            
            <div className="flex items-center gap-4 mb-6 pb-6 border-b border-olive/10">
              <div className="w-16 h-16 rounded-full bg-olive/10 flex items-center justify-center">
                <Dog className="w-8 h-8 text-olive" />
              </div>
              <div>
                <h3 className="font-heading text-xl text-charcoal">{paymentDetails.puppy?.name}</h3>
                <p className="text-stone">{paymentDetails.puppy?.breed}</p>
              </div>
            </div>

            <div className="space-y-4">
              <div className="flex items-center gap-3">
                <Calendar className="w-5 h-5 text-olive" />
                <div>
                  <p className="text-sm text-stone">Program</p>
                  <p className="font-medium text-charcoal">{paymentDetails.enrollment?.program_name}</p>
                </div>
              </div>
              
              <div className="flex items-center gap-3">
                <Clock className="w-5 h-5 text-olive" />
                <div>
                  <p className="text-sm text-stone">Start Date</p>
                  <p className="font-medium text-charcoal">{paymentDetails.enrollment?.start_date}</p>
                </div>
              </div>

              <div className="flex items-center gap-3">
                <Calendar className="w-5 h-5 text-olive" />
                <div>
                  <p className="text-sm text-stone">Schedule</p>
                  <p className="font-medium text-charcoal">{paymentDetails.enrollment?.day_of_week}s for 4 weeks</p>
                </div>
              </div>
            </div>

            <div className="mt-6 pt-6 border-t border-olive/10">
              <div className="flex justify-between items-center">
                <span className="text-lg text-charcoal">Total</span>
                <span className="font-heading text-3xl text-olive">$440 AUD</span>
              </div>
            </div>
          </motion.div>

          {/* Payment Section */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.2 }}
            className="card-premium"
          >
            <h2 className="font-heading text-2xl text-charcoal mb-6">Select Payment Method</h2>

            {processing ? (
              <div className="text-center py-12">
                <div className="animate-spin rounded-full h-12 w-12 border-4 border-olive border-t-transparent mx-auto mb-4" />
                <p className="text-stone">Processing...</p>
              </div>
            ) : !selectedMethod ? (
              <div className="space-y-3">
                {/* Credit Card / Stripe */}
                {paymentMethods?.methods?.includes('stripe') && (
                  <button
                    onClick={() => setSelectedMethod('stripe')}
                    className="w-full p-4 border-2 border-olive/20 rounded-xl hover:border-olive transition-colors flex items-center gap-4 text-left"
                    data-testid="select-stripe"
                  >
                    <div className="w-12 h-12 rounded-full bg-olive/10 flex items-center justify-center">
                      <CreditCard className="w-6 h-6 text-olive" />
                    </div>
                    <div>
                      <p className="font-medium text-charcoal">Credit / Debit Card</p>
                      <p className="text-sm text-stone">Pay securely with Stripe</p>
                    </div>
                  </button>
                )}

                {/* PayPal */}
                {paypalClientId && (
                  <button
                    onClick={() => setSelectedMethod('paypal')}
                    className="w-full p-4 border-2 border-olive/20 rounded-xl hover:border-olive transition-colors flex items-center gap-4 text-left"
                    data-testid="select-paypal"
                  >
                    <div className="w-12 h-12 rounded-full bg-[#003087]/10 flex items-center justify-center">
                      <span className="font-bold text-[#003087] text-sm">PP</span>
                    </div>
                    <div>
                      <p className="font-medium text-charcoal">PayPal</p>
                      <p className="text-sm text-stone">Pay with your PayPal account</p>
                    </div>
                  </button>
                )}

                {/* Bank Transfer */}
                {paymentMethods?.methods?.includes('bank_transfer') && paymentMethods?.bank_details?.available && (
                  <button
                    onClick={() => setSelectedMethod('bank_transfer')}
                    className="w-full p-4 border-2 border-olive/20 rounded-xl hover:border-olive transition-colors flex items-center gap-4 text-left"
                    data-testid="select-bank-transfer"
                  >
                    <div className="w-12 h-12 rounded-full bg-olive/10 flex items-center justify-center">
                      <Building2 className="w-6 h-6 text-olive" />
                    </div>
                    <div>
                      <p className="font-medium text-charcoal">Bank Transfer</p>
                      <p className="text-sm text-stone">Direct deposit to our account</p>
                    </div>
                  </button>
                )}
              </div>
            ) : selectedMethod === 'stripe' ? (
              <div className="space-y-4">
                <Button
                  variant="ghost"
                  onClick={() => setSelectedMethod(null)}
                  className="text-stone hover:text-charcoal"
                >
                  ← Back to payment options
                </Button>
                
                <div className="text-center py-4">
                  <CreditCard className="w-12 h-12 text-olive mx-auto mb-4" />
                  <p className="text-charcoal mb-4">You&apos;ll be redirected to Stripe&apos;s secure checkout page</p>
                  <Button
                    onClick={handleStripeCheckout}
                    className="btn-primary w-full"
                    data-testid="stripe-checkout-btn"
                  >
                    Pay with Card - $440 AUD
                  </Button>
                </div>
              </div>
            ) : selectedMethod === 'paypal' ? (
              <div className="space-y-4">
                <Button
                  variant="ghost"
                  onClick={() => setSelectedMethod(null)}
                  className="text-stone hover:text-charcoal"
                >
                  ← Back to payment options
                </Button>
                
                <PayPalScriptProvider options={{ 
                  "client-id": paypalClientId,
                  currency: "AUD"
                }}>
                  <PayPalButtons
                    style={{ layout: "vertical", shape: "pill" }}
                    createOrder={(data, actions) => {
                      return actions.order.create({
                        purchase_units: [{
                          amount: {
                            value: "440.00",
                            currency_code: "AUD"
                          },
                          description: `${paymentDetails.enrollment?.program_name} - ${paymentDetails.puppy?.name}`
                        }]
                      });
                    }}
                    onApprove={async (data, actions) => {
                      const order = await actions.order.capture();
                      handlePaymentSuccess(order.id);
                    }}
                    onError={() => {
                      toast.error("Payment failed. Please try again.");
                    }}
                    data-testid="paypal-buttons"
                  />
                </PayPalScriptProvider>
              </div>
            ) : selectedMethod === 'bank_transfer' ? (
              <div className="space-y-4">
                <Button
                  variant="ghost"
                  onClick={() => setSelectedMethod(null)}
                  className="text-stone hover:text-charcoal"
                >
                  ← Back to payment options
                </Button>
                
                <div className="text-center py-4">
                  <Building2 className="w-12 h-12 text-olive mx-auto mb-4" />
                  <p className="text-charcoal mb-2">Bank Transfer</p>
                  <p className="text-sm text-stone mb-4">
                    We&apos;ll send you our bank details. Your spot will be confirmed once we receive your payment.
                  </p>
                  <Button
                    onClick={handleBankTransfer}
                    className="btn-primary w-full"
                    data-testid="bank-transfer-btn"
                  >
                    Get Bank Details
                  </Button>
                </div>
              </div>
            ) : null}

            <div className="mt-6 pt-6 border-t border-olive/10">
              <div className="flex items-center gap-2 text-sm text-stone">
                <CheckCircle className="w-4 h-4 text-olive" />
                <span>Secure payment</span>
              </div>
              <div className="flex items-center gap-2 text-sm text-stone mt-2">
                <CheckCircle className="w-4 h-4 text-olive" />
                <span>Your spot is reserved upon payment</span>
              </div>
            </div>
          </motion.div>
        </div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.3 }}
          className="text-center mt-8"
        >
          <p className="text-stone text-sm">
            Questions? Contact us at{" "}
            <a href="mailto:thepackstandard@gmail.com" className="text-olive hover:underline">
              thepackstandard@gmail.com
            </a>
          </p>
        </motion.div>
      </main>
    </div>
  );
};

export default CompletePaymentPage;
